import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';
import { PeopleSdk } from 'lib-klab-sdks';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { ProductivityRulesPort } from '../../../app/ports/people/productivity-rules.port';
import { CrudMockAction } from '../../config/crud-mock-action.type';
import { EnvMicroPeople } from '../../config/env.micro.people';
import { PEOPLE_ENV } from '../../config/micros-env.tokens';
import { productivityRulesSdkMock } from '../../mocks/people/productivity-rules.mock';

@Injectable({ providedIn: 'root' })
export class ProductivityRulesPortAdapter implements ProductivityRulesPort {
  constructor(
    private productivityRulesApi: PeopleSdk.ProductivityRulesService,
    private appEvents: AppEventsService,
    @Optional() private toast: CoreUIToastV2Service,
    @Inject(PEOPLE_ENV) private env: EnvMicroPeople,
  ) { }

  getAllProductivityRules(
    page?: number,
    size?: number,
    type?: PeopleSdk.ActivityTypeEnum,
    departmentId?: number,
  ): Observable<PeopleSdk.PagedProductivityRuleResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-productivity-rules-list');

    const source$ = this.shouldMock('getAll')
      ? of(
        productivityRulesSdkMock.getAllProductivityRules(
          page ?? 0,
          size ?? 20,
          type,
          departmentId,
        ),
      ).pipe(delay(360))
      : this.productivityRulesApi.getAllProductivityRules(page, size, type, departmentId);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-productivity-rules-list')),
    );
  }

  getProductivityRuleById(
    productivityRuleId: number,
  ): Observable<PeopleSdk.ProductivityRuleResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-productivity-rule-getById');

    const source$ = this.shouldMock('getById')
      ? of(
        productivityRulesSdkMock.getProductivityRuleById(productivityRuleId),
      ).pipe(delay(360))
      : this.productivityRulesApi.getProductivityRuleById(productivityRuleId);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() =>
        this.appEvents.loaderVisiblePublish(false, 'people-productivity-rule-getById'),
      ),
    );
  }

  createProductivityRule(
    payload: PeopleSdk.ProductivityRuleCreateRequest,
  ): Observable<PeopleSdk.ProductivityRuleResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-productivity-rule-create');

    const source$ = this.shouldMock('create')
      ? of(productivityRulesSdkMock.createProductivityRule(payload)).pipe(delay(360))
      : this.productivityRulesApi.createProductivityRule(payload);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-productivity-rule-create')),
    );
  }

  updateProductivityRule(
    productivityRuleId: number,
    payload: PeopleSdk.ProductivityRuleUpdateRequest,
  ): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-productivity-rule-update');

    const source$ = this.shouldMock('update')
      ? of(
        productivityRulesSdkMock.updateProductivityRule(
          productivityRuleId,
          payload,
        ) as unknown as void,
      ).pipe(delay(360))
      : this.productivityRulesApi.updateProductivityRule(productivityRuleId, payload);

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-productivity-rule-update')),
    );
  }

  deleteProductivityRuleById(productivityRuleId: number): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-productivity-rule-delete');

    const source$ = this.shouldMock('delete')
      ? of(
        productivityRulesSdkMock.deleteProductivityRuleById(
          productivityRuleId,
        ) as unknown as void,
      ).pipe(delay(360))
      : this.productivityRulesApi.deleteProductivityRuleById(productivityRuleId);

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-productivity-rule-delete')),
    );
  }

  exportProductivityRules(
    payload: PeopleSdk.ProductivityRuleExportRequest,
  ): Observable<Blob> {
    this.appEvents.loaderVisiblePublish(true, 'people-productivity-rules-export');

    return this.productivityRulesApi.exportProductivityRules(payload).pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() =>
        this.appEvents.loaderVisiblePublish(false, 'people-productivity-rules-export'),
      ),
    );
  }

  private shouldMock(action: CrudMockAction): boolean {
    return Boolean(this.env?.useMocks || this.env?.mock?.productivityRules?.[action]);
  }

  private showErrorToast(err: any) {
    if (!this.toast) return;

    const status = err?.status;
    const mapMsg: Record<number, string> = {
      400: 'Solicitud inválida',
      401: 'No autorizado',
      404: 'No encontrado',
      429: 'Demasiadas solicitudes',
      500: 'Error interno del servidor',
    };

    this.toast.show({
      data: {
        title: 'Error',
        msg: mapMsg[status] || 'Error inesperado',
        direction: CoreUIToastDirection.topCenter,
        autoClose: true,
      },
    });
  }
}
